import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  List,
  Typography,
  Button,
  Tag,
  Space,
  Switch,
  Badge,
  Empty,
  Avatar,
  Divider,
  message,
  Input,
  Select,
  Alert,
} from 'antd';
import {
  BellOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  TrophyOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { deliveryService } from '../services/deliveryService';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // eslint-disable-line no-unused-vars
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    // Push notifications
    newOrderAssignments: true,
    paymentConfirmations: true,
    customerMessages: true,
    achievementsAndBonuses: true,
    // Email notifications
    weeklyEarningsSummary: true,
    monthlyPerformanceReport: false,
  });

  // Load notification settings from localStorage
  const loadNotificationSettings = () => {
    try {
      const savedSettings = localStorage.getItem('deliveryNotificationSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setNotificationSettings((prevSettings) => ({
          ...prevSettings,
          ...settings,
        }));
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  // Save notification settings to localStorage
  const saveNotificationSettings = (newSettings) => {
    try {
      localStorage.setItem('deliveryNotificationSettings', JSON.stringify(newSettings));
      setNotificationSettings(newSettings);
    } catch (error) {
      console.error('Failed to save notification settings:', error);
    }
  };

  // Handle notification setting change
  const handleNotificationSettingChange = (setting, value) => {
    const newSettings = {
      ...notificationSettings,
      [setting]: value,
    };
    saveNotificationSettings(newSettings);
  };

  // Load settings on component mount
  useEffect(() => {
    loadNotificationSettings();
  }, []);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await deliveryService.getNotifications();
      if (response.data.success && Array.isArray(response.data.data)) {
        // Map backend fields to frontend expected fields
        const mappedNotifications = response.data.data.map((notification) => ({
          ...notification,
          read: notification.is_read, // Map is_read to read
          time: notification.created_at
            ? new Date(notification.created_at).toLocaleString()
            : notification.time,
        }));
        setNotifications(mappedNotifications);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setError('Failed to load notifications. Please try again later.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await deliveryService.markNotificationAsRead(notificationId);
      setNotifications(
        notifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true, is_read: true } // Update both fields
            : notification,
        ),
      );
      message.success('Marked as read');
    } catch (error) {
      console.error('Failed to mark as read:', error);
      message.error('Failed to mark notification as read');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      // Mark each notification as read
      const unreadNotifications = notifications.filter((n) => !n.read);
      await Promise.all(
        unreadNotifications.map((n) => deliveryService.markNotificationAsRead(n.id)),
      );

      setNotifications(notifications.map((notification) => ({ ...notification, read: true })));
      message.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      message.error('Failed to mark all notifications as read');
    }
  };

  // Delete notification
  const deleteNotification = (id) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
    message.success('Notification deleted');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <ShoppingCartOutlined />;
      case 'payment':
        return <DollarOutlined />;
      case 'achievement':
        return <TrophyOutlined />;
      case 'message':
        return <MessageOutlined />;
      case 'system':
        return <ExclamationCircleOutlined />;
      case 'promotion':
        return <BellOutlined />;
      default:
        return <BellOutlined />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'order':
        return '#1890ff';
      case 'payment':
        return '#52c41a';
      case 'achievement':
        return '#faad14';
      case 'message':
        return '#722ed1';
      case 'system':
        return '#ff4d4f';
      case 'promotion':
        return '#13c2c2';
      default:
        return '#1890ff';
    }
  };

  const handleNotificationAction = async (notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }

    // Extract action from notification data field
    const notificationData = notification.data || {};
    const action = notificationData.action;
    const actionData = notificationData.data || {};

    // Navigate to the corresponding page based on notification type and data
    switch (notification.type) {
      case 'order':
        if (actionData?.order_id) {
          navigate(`/orders?order=${actionData.order_id}`);
        } else {
          navigate('/orders');
        }
        break;
      case 'delivery':
        if (actionData?.order_id) {
          navigate(`/orders?order=${actionData.order_id}`);
        } else {
          navigate('/orders');
        }
        break;
      case 'payment':
        navigate('/earnings');
        break;
      case 'message':
        if (actionData?.order_id) {
          navigate(`/orders?order=${actionData.order_id}&tab=chat`);
        } else {
          navigate('/orders');
        }
        break;
      case 'promotion':
        navigate('/earnings');
        break;
      case 'admin':
        navigate('/profile');
        break;
      case 'system':
        // System notifications might have specific navigation
        if (action === 'view_profile') {
          navigate('/profile');
        } else if (action === 'view_earnings') {
          navigate('/earnings');
        } else if (action === 'view_analytics') {
          navigate('/analytics');
        }
        break;
      default:
        // For unknown types, check if there's a specific action in data
        if (action === 'view_order' && actionData?.order_id) {
          navigate(`/orders?order=${actionData.order_id}`);
        } else if (action === 'view_earnings') {
          navigate('/earnings');
        } else if (action === 'view_profile') {
          navigate('/profile');
        } else if (action === 'view_analytics') {
          navigate('/analytics');
        }
        break;
    }
  };

  const sortedNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    return [...notifications].sort((a, b) => {
      // Unread first, then by date
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (!Array.isArray(sortedNotifications)) return [];

    return sortedNotifications.filter((notification) => {
      if (!notification || typeof notification !== 'object') return false;

      const matchesSearch =
        !searchTerm ||
        (notification.title &&
          notification.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (notification.message &&
          notification.message.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType = !typeFilter || notification.type === typeFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'unread' && !notification.read) ||
        (statusFilter === 'read' && notification.read);

      const matchesLegacyFilter =
        filter === 'all' ||
        (filter === 'unread' && !notification.read) ||
        (filter === 'order' && notification.type === 'order') ||
        (filter === 'payment' && notification.type === 'payment') ||
        (filter === 'achievement' && notification.type === 'achievement') ||
        (filter === 'message' && notification.type === 'message') ||
        (filter === 'system' && notification.type === 'system');

      return matchesSearch && matchesType && matchesStatus && matchesLegacyFilter;
    });
  }, [sortedNotifications, searchTerm, typeFilter, statusFilter, filter]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  if (error) {
    return (
      <Alert
        message="Error Loading Notifications"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchNotifications}>
            Retry
          </Button>
        }
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <Badge count={unreadCount} size="small">
          <BellOutlined /> Notifications
        </Badge>
      </Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Stay updated with your delivery assignments, payments, and important updates
      </Paragraph>

      {/* Statistics Cards */}
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
              {unreadCount}
            </div>
            <div style={{ color: '#666' }}>Unread</div>
          </div>
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
              {Array.isArray(filteredNotifications)
                ? filteredNotifications.length - unreadCount
                : 0}
            </div>
            <div style={{ color: '#666' }}>Read</div>
          </div>
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
              {Array.isArray(filteredNotifications) ? filteredNotifications.length : 0}
            </div>
            <div style={{ color: '#666' }}>Total</div>
          </div>
        </Card>
      </div>

      <Card>
        {/* Filters and Actions */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Space>
            <Input
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Filter by type"
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="order">Orders</Option>
              <Option value="payment">Payments</Option>
              <Option value="achievement">Achievements</Option>
              <Option value="message">Messages</Option>
              <Option value="system">System</Option>
              <Option value="promotion">Promotions</Option>
            </Select>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="unread">Unread</Option>
              <Option value="read">Read</Option>
            </Select>
          </Space>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchNotifications} loading={loading}>
              Refresh
            </Button>
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark All Read
            </Button>
          </Space>
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <Empty
            image={<BellOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
            description={
              <div>
                <Title level={4}>No notifications</Title>
                <Paragraph>
                  {filter === 'all'
                    ? "You don't have any notifications yet."
                    : `No ${filter} notifications found.`}
                </Paragraph>
              </div>
            }
          />
        ) : (
          <List
            loading={loading}
            dataSource={filteredNotifications}
            renderItem={(notification) => (
              <List.Item
                style={{
                  backgroundColor: !notification.read ? '#f6ffed' : '#fafafa',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: !notification.read ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => handleNotificationAction(notification)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = !notification.read
                    ? '#d9f7be'
                    : '#f0f0f0';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = !notification.read
                    ? '#f6ffed'
                    : '#fafafa';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                actions={[
                  !notification.read && (
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                    >
                      Mark as read
                    </Button>
                  ),
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    Delete
                  </Button>,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{ backgroundColor: getNotificationColor(notification.type) }}
                      icon={getNotificationIcon(notification.type)}
                    />
                  }
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontWeight: !notification.read ? 'bold' : 'normal' }}>
                        {notification.title}
                      </span>
                      <Space>
                        <Tag color={getNotificationColor(notification.type)}>
                          {notification.type?.toUpperCase()}
                        </Tag>
                        {notification.read ? (
                          <Tag color="default">READ</Tag>
                        ) : (
                          <Tag color="processing">UNREAD</Tag>
                        )}
                      </Space>
                    </div>
                  }
                  description={
                    <div>
                      <p style={{ margin: '4px 0', color: '#666' }}>{notification.message}</p>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {notification.created_at
                          ? new Date(notification.created_at).toLocaleString()
                          : notification.time}
                      </span>
                      {notification.data && (
                        <div style={{ marginTop: '8px' }}>
                          {notification.data.data?.order_id && (
                            <Tag color="blue">Order #{notification.data.data.order_id}</Tag>
                          )}
                          {notification.data.data?.amount && (
                            <Tag color="orange">
                              NPR {parseFloat(notification.data.data.amount).toFixed(2)}
                            </Tag>
                          )}
                          {notification.data.data?.customer_name && (
                            <Tag color="green">{notification.data.data.customer_name}</Tag>
                          )}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
            locale={{
              emptyText: 'No notifications found',
            }}
          />
        )}
      </Card>

      {/* Notification Settings Modal (placeholder) */}
      <Card title="Notification Settings" style={{ marginTop: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={5}>Push Notifications</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>New order assignments</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Get notified when new orders are assigned to you
                </Text>
              </div>
              <Switch
                checked={notificationSettings.newOrderAssignments}
                onChange={(checked) =>
                  handleNotificationSettingChange('newOrderAssignments', checked)
                }
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>Payment confirmations</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Get notified when payments are processed
                </Text>
              </div>
              <Switch
                checked={notificationSettings.paymentConfirmations}
                onChange={(checked) =>
                  handleNotificationSettingChange('paymentConfirmations', checked)
                }
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>Customer messages</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Get notified when customers send messages
                </Text>
              </div>
              <Switch
                checked={notificationSettings.customerMessages}
                onChange={(checked) => handleNotificationSettingChange('customerMessages', checked)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>Achievements and bonuses</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Get notified about new achievements and bonuses
                </Text>
              </div>
              <Switch
                checked={notificationSettings.achievementsAndBonuses}
                onChange={(checked) =>
                  handleNotificationSettingChange('achievementsAndBonuses', checked)
                }
              />
            </div>
          </Space>
        </div>

        <Divider />

        <div>
          <Title level={5}>Email Notifications</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>Weekly earnings summary</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Receive weekly summary of your earnings
                </Text>
              </div>
              <Switch
                checked={notificationSettings.weeklyEarningsSummary}
                onChange={(checked) =>
                  handleNotificationSettingChange('weeklyEarningsSummary', checked)
                }
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div>Monthly performance report</div>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  Receive detailed monthly performance reports
                </Text>
              </div>
              <Switch
                checked={notificationSettings.monthlyPerformanceReport}
                onChange={(checked) =>
                  handleNotificationSettingChange('monthlyPerformanceReport', checked)
                }
              />
            </div>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default Notifications;
